/**
 * The lead vendor's whole CRM (spec 1.2): send a lead, and see the policies its leads became
 * ("Lead Vendor Opportunities List"). The sale, never the commission, the notes or the number.
 */
import { toUsDate } from "@tlcrm/shared";
import { useState } from "react";

import { useVendorPolicies } from "../api/hooks";
import { useMe, useSession } from "../auth/session";
import { Icon } from "../components/Icon";
import { EmptyState, ErrorState, PostSaleMark, StageMark } from "../components/ui";
import { NewLeadModal } from "../forms/NewLead";
import { money, when } from "../lib/format";

export function VendorHome() {
  const { me } = useMe();
  const { signOut } = useSession();
  const policies = useVendorPolicies();
  const [sending, setSending] = useState(false);
  return (
    <div className="vendor">
      <header className="topbar">
        <div className="nav-brand">
          <span className="nav-mark">TL</span>
          <div>
            <b>Tomorrow</b>
            <span>{me.user.leadVendor?.name ?? "Lead vendor"}</span>
          </div>
        </div>
        <div className="topbar-spacer" />
        <button className="btn btn-primary" onClick={() => setSending(true)}>
          <Icon name="plus" /> Send a lead
        </button>
        <button
          className="icon-btn"
          onClick={() => void signOut()}
          aria-label="Sign out"
          title="Sign out"
        >
          <Icon name="logOut" />
        </button>
      </header>
      <main className="page">
        <div className="page-head">
          <div>
            <h1>Policies from your leads</h1>
            <p>Every sale made from a lead you sent, newest first.</p>
          </div>
        </div>
        {policies.isError ? (
          <ErrorState error={policies.error} onRetry={() => void policies.refetch()} />
        ) : (
          <div className="table-wrap">
            <table className="grid grid-compact">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Lead sent</th>
                  <th>Date Sold</th>
                  <th>Stage</th>
                  <th>Post-Sale</th>
                  <th>Carrier</th>
                  <th>Policy Type</th>
                  <th className="num">Total Premium</th>
                </tr>
              </thead>
              <tbody>
                {(policies.data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td className="primary">{p.customerName}</td>
                    <td>{p.leadReceivedAt ? when(p.leadReceivedAt) : "–"}</td>
                    <td className="mono">{toUsDate(p.dateSold)}</td>
                    <td>
                      <StageMark stage={p.stage} />
                    </td>
                    <td>
                      <PostSaleMark status={p.postSaleStatus} />
                    </td>
                    <td>{p.carrier ?? "–"}</td>
                    <td>{p.product ?? "–"}</td>
                    <td className="num mono">{money(p.totalPremium, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {policies.data?.length === 0 && (
              <EmptyState icon="briefcase" title="No policies from your leads yet" />
            )}
          </div>
        )}
      </main>
      {sending && <NewLeadModal onClose={() => setSending(false)} />}
    </div>
  );
}
